import { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const POLL_INTERVAL_MS = 60 * 1000;

export type ServiceStatus = 'healthy' | 'unhealthy';

export interface ServiceHealthEntry {
  status: ServiceStatus;
  responseTimeMs: number;
}

export type OverallStatus = 'healthy' | 'degraded' | 'unhealthy' | 'loading';

export interface ServiceHealthState {
  services: Record<string, ServiceHealthEntry>;
  overall: OverallStatus;
}

interface HealthStatusApiResponse {
  services: {
    registry: ServiceHealthEntry;
    search: ServiceHealthEntry;
    searchGraphql: ServiceHealthEntry;
    auth: ServiceHealthEntry;
  };
}

function computeOverall(
  services: Record<string, ServiceHealthEntry>,
): OverallStatus {
  const entries = Object.values(services);
  if (entries.length === 0) return 'loading';
  const healthyCount = entries.filter((e) => e.status === 'healthy').length;
  if (healthyCount === entries.length) return 'healthy';
  if (healthyCount === 0) return 'unhealthy';
  return 'degraded';
}

export function useServiceHealth(): ServiceHealthState {
  const [state, setState] = useState<ServiceHealthState>({
    services: {},
    overall: 'loading',
  });
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchHealth = () => {
    axios
      .get<HealthStatusApiResponse>('/api/health-status', { timeout: 15000 })
      .then((res) => {
        const services = res.data.services as Record<
          string,
          ServiceHealthEntry
        >;
        setState({
          services,
          overall: computeOverall(services),
        });
      })
      .catch(() => {
        setState((prev) => ({
          ...prev,
          overall: 'unhealthy',
          services: prev.services,
        }));
      });
  };

  useEffect(() => {
    fetchHealth();
    intervalRef.current = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return state;
}
